import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowUp, Loader2, Cpu, Globe, ExternalLink, History, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const SUGGESTIONS = [
  "What changed in the last 30 days?",
  "How do I improve my funding readiness score?",
  "What capital opportunities am I prequalified for?",
  "Why is my business address flagged?",
];

const WEB_SUGGESTIONS = [
  "What are current SBA 7(a) loan interest rates?",
  "Latest small business funding programs this quarter",
  "Recent changes to business credit reporting regulations",
  "What are typical requirements for a business line of credit?",
];

const CAPABILITIES = ["GENERAL_REASONING", "FINANCIAL_ANALYSIS", "DEEP_REASONING", "RAG_QA"];

export function AskModal({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [capability, setCapability] = useState("GENERAL_REASONING");
  const [session, setSession] = useState(null);
  const [mode, setMode] = useState("ai"); // ai | web
  const [webStatus, setWebStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 60);
      api.get("/ai/web-research/status").then(({ data }) => setWebStatus(data)).catch(() => setWebStatus({ connected: false }));
      api.get("/ai/web-research/history").then(({ data }) => setHistory(data.items)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    if (mode === "web" && msg.length < 2) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      if (mode === "web") {
        if (webStatus && webStatus.connected === false) {
          setMessages((m) => [...m, { role: "ai", text: webStatus.note || "Live web research is not connected yet (PROVIDER APPROVAL PENDING). Add a Perplexity API key to activate.", error: true }]);
        } else {
          const { data } = await api.post("/ai/web-research/ask", { query: msg });
          setMessages((m) => [...m, { role: "ai", text: data.answer, model: data.model, provider: data.provider, citations: data.citations }]);
          api.get("/ai/web-research/history").then(({ data }) => setHistory(data.items)).catch(() => {});
        }
      } else {
        const { data } = await api.post("/ai/ask", { message: msg, session_id: session, capability });
        setSession(data.session_id);
        setMessages((m) => [...m, { role: "ai", text: data.reply, model: data.model, provider: data.provider }]);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const upgrade = e?.response?.status === 402;
      setMessages((m) => [...m, { role: "ai", upgrade, text: detail ? formatApiErrorDetail(detail) : "I couldn't reach the MMT AI layer just now. Please try again.", error: !upgrade }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4 pt-[8vh] backdrop-blur-sm" onClick={onClose}>
      <div
        className="card-spotlight flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="ask-modal"
      >
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#1a5fd0] to-[#0b1327] ring-1 ring-white/10">
            <Sparkles size={16} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="font-head text-sm font-bold text-slate-900">Ask MMT — Ava Concierge</div>
            <div className="font-mono text-[10px] text-slate-400">
              {mode === "web" ? "Live web research · Perplexity Sonar · cited sources" : "Multi-model AI router · grounded on your authorized data"}
            </div>
          </div>
          <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="ask-mode-toggle">
            <button onClick={() => setMode("ai")} data-testid="ask-mode-ai"
              className={`rounded-md px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${mode === "ai" ? "bg-white text-[#1a5fd0] shadow-sm" : "text-slate-400"}`}>
              MMT AI
            </button>
            <button onClick={() => setMode("web")} data-testid="ask-mode-web"
              className={`flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${mode === "web" ? "bg-white text-[#1a5fd0] shadow-sm" : "text-slate-400"}`}>
              <Globe size={10} /> Web
            </button>
          </div>
          {mode === "ai" && (
            <select
              value={capability}
              onChange={(e) => setCapability(e.target.value)}
              data-testid="ask-capability-select"
              className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[10px] text-slate-500"
            >
              {CAPABILITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="ask-close-btn">
            <X size={18} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4">
          {messages.length === 0 && (
            <div className="space-y-4">
              {mode === "web" && webStatus?.connected === false && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[12px] text-amber-800" data-testid="web-pending-banner">
                  <b>Provider approval pending.</b> Live web research activates once a Perplexity API key is configured. You can still explore the interface.
                </div>
              )}
              <p className="text-sm text-slate-500">
                {mode === "web"
                  ? "Ask a market, lender, or regulatory question and get current, cited answers from the web."
                  : "Ask anything across your authorized business, financial, and document data."}
              </p>
              <div className="grid gap-2">
                {(mode === "web" ? WEB_SUGGESTIONS : SUGGESTIONS).map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="ask-suggestion"
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-left text-[13px] text-slate-700 transition-colors hover:border-[#1a5fd0]/40 hover:bg-white"
                  >
                    {mode === "web" ? <Globe size={13} className="text-[#1a5fd0]" /> : <Sparkles size={13} className="text-[#1a5fd0]" />} {s}
                  </button>
                ))}
              </div>
              {mode === "web" && history.length > 0 && (
                <div className="pt-1" data-testid="web-history">
                  <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">Recent research</div>
                  <div className="space-y-1.5">
                    {history.slice(0, 5).map((h, i) => (
                      <button key={`${h.query}-${i}`} onClick={() => send(h.query)} data-testid="web-history-item"
                        className="flex w-full items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-left text-[12px] text-slate-600 transition-colors hover:border-[#1a5fd0]/40 hover:bg-white">
                        <History size={12} className="shrink-0 text-slate-400" /> <span className="truncate">{h.query}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
                  m.role === "user" ? "bg-[#1a5fd0] text-white" : m.error ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-800"
                }`}>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  {m.upgrade && (
                    <Link to="/pricing" onClick={onClose} data-testid="ask-upgrade-btn"
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#0f172a] px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[#1a5fd0]">
                      Upgrade to Expand <ArrowRight size={13} />
                    </Link>
                  )}
                  {m.model && (
                    <div className="mt-1.5 flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-slate-400">
                      <Cpu size={9} /> {m.provider} · {m.model}
                    </div>
                  )}
                  {m.citations?.length > 0 && (
                    <div className="mt-2 space-y-1 border-t border-slate-200 pt-2">
                      <div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Sources</div>
                      {m.citations.slice(0, 6).map((url, ci) => (
                        <a key={`${url}-${ci}`} href={url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1 truncate text-[11px] text-[#1a5fd0] hover:underline">
                          <ExternalLink size={10} className="shrink-0" /> <span className="truncate">{url}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Loader2 size={15} className="animate-spin text-[#1a5fd0]" /> Routing to the right model...
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-100 p-3">
          <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus-within:border-[#1a5fd0]">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Search or ask MMT anything..."
              data-testid="ask-input"
              className="flex-1 resize-none bg-transparent py-1 text-sm text-slate-800 outline-none placeholder:text-slate-400"
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              data-testid="ask-send-btn"
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1a5fd0] text-white transition-opacity disabled:opacity-40"
            >
              <ArrowUp size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
